import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import type { ModalContextType } from "@/components/core/modals/types";
import { useModal } from "@/components/core/modals/use-modal.ts";
import Tooltip from "@/components/core/tooltip";
import { FileViewer } from "@/components/document-viewer/file-viewer.tsx";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import type { Media } from "@/features/dashboard/types/media.ts";
import { downloadFile } from "@/utils/download-file";
import { formatDate } from "@/utils/format-date.ts";
import { formatNumber } from "@/utils/format-number.ts";
import { IconDatabaseExclamation, IconDownload, IconEye, IconFileTypePdf, IconPlus, IconPrinter, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import CaseFileUploadModal from "../case-classification/case-file-upload-modal";
import { useCaseRecordMediaDeleteMutation, useCaseRecordPaymentProofMutation } from "../case-record/api/queries/case-records-query";
import type { CaseRecord } from "../case-record/types/case-records";

interface FileUploadButtonProps {
	modal: ModalContextType;
	data: CaseRecord;
	setFiles: React.Dispatch<React.SetStateAction<File[]>>;
	files: File[];
}

function FileUploadButton({ modal, data }: FileUploadButtonProps) {
	const [files, setFiles] = useState<File[]>([]);
	const paymentMutation = useCaseRecordPaymentProofMutation();

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(event.target.files || []);
		if (selectedFiles.length > 0) {
			const updatedFiles = [...files, ...selectedFiles];
			setFiles(updatedFiles);

			const paymentModal = modal.openModal({
				title: "Upload Payment Proof",
				size: "lg",
				showHeader: true,
				showFooter: false,
				component: (
					<CaseFileUploadModal
						onSubmit={async (value) => {
							await paymentMutation.mutateAsync({
								id: data.id,
								payment: value,
							});
							setFiles([]);
							modal.closeModal(paymentModal);
						}}
						initialFiles={updatedFiles}
					/>
				),
			});
		}
	};

	return (
		<Button className="relative" type="button" size="sm">
			<input type="file" multiple className="absolute opacity-0 inset-0" onChange={handleFileChange} />
			<IconPlus size={14} stroke={2} />
			<span className="">Add File</span>
		</Button>
	);
}

export default function CasePaymentProof({
	data,
	title = "Payment Receipts",
	allowRemove = true,
	allowAdd = true,
	allowPreview = true,
	allowPrint = false,
	allowDownload = false,
	isReadOnly = false,
}: {
	data: CaseRecord;
	title: string;
	counts: number;
	allowRemove: boolean;
	allowAdd: boolean;
	allowPreview: boolean;
	allowPrint: boolean;
	allowDownload: boolean;
	isReadOnly: boolean;
}) {
	const modal = useModal();
	const documentTokenMutation = useDocumentConfigMutation();
	const paymentdeleteMutation = useCaseRecordMediaDeleteMutation();
	const handleDownload = (attachment: Media) => {
		downloadFile(attachment.original_url, attachment.file_name);
	};
	const [files, setFiles] = useState<File[]>([]);

	const handleClick = async (attachment: Media) => {
		const fileExtension = attachment.file_name.split(".").pop();

		let response = null;

		if (!["image/jpeg", "image/png", "application/pdf", "png", "pdf", "jpeg", "video/mp4", "mp4"].includes(fileExtension)) {
			response = await documentTokenMutation.mutateAsync({
				file_type: fileExtension,
				title: attachment.name,
				url: attachment.original_url,
				mode: "view",
			});
		}

		modal.openModal({
			title: "Default Modal",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} token={response ? response?.token : null} options={response ? response?.options : null} />,
		});
	};

	const handleRemove = async (caseRecordId: string, mediaId: string) => {
		const mod = modal.openModal({
			type: "confirm",
			title: "Confirm Archive",
			variant: "red",
			description: "Are you sure you want to remove this media?",
			onConfirm: async () => {
				await paymentdeleteMutation.mutateAsync({ caseRecordId, mediaId });
				modal.closeModal(mod);
			},
		});
	};

	return (
		<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
			<div className="flex flex-row items-center justify-between w-full p-4 gap-4 bg-slate-50">
				<span className="text-base font-semibold">{title}</span>
				{!isReadOnly && allowAdd && <FileUploadButton modal={modal} data={data} files={files} setFiles={setFiles} />}
			</div>

			{data?.payment?.length !== 0 ? (
				<div className="flex flex-col w-full gap-2">
					{/* <div className="flex flex-col w-full px-4 pt-4">
            <span className="font-bold text-sm text-foreground-dark">Uploaded ({data?.payment?.length}) file/s</span>
            <span className="relative inline-flex text-sm text-foreground/70">
              Review the uploaded files carefully before confirming. Drag 'n' drop files on the drop zone above to add more files.
            </span>
          </div> */}

					<div className="p-4 flex flex-col w-full gap-2">
						{data?.payment?.map((payment: Media, index) => (
							<div key={index} className="flex flex-col w-full gap-2">
								<div className="flex flex-row w-full gap-2">
									<div className="inline-flex justify-center items-center w-[50px] h-[50px] rounded-md bg-red-500 text-white">
										<IconFileTypePdf size={24} stroke={1.5} />
									</div>
									<div className="inline-flex flex-col w-auto">
										<button type="button" onClick={() => handleClick(payment)} className="text-left text-base font-semibold text-blue-600 hover:text-blue-500">
											{payment.file_name}
										</button>
										<span className="text-sm text-foreground/70">Uploaded on {formatDate(payment.created_at)}</span>
										<span className="text-sm text-foreground/70">{formatNumber(payment.size, "0,0b")}</span>
									</div>
									<div className="inline-flex w-auto gap-3 ml-auto">
										<div className="inline-flex flex-row w-auto gap-1">
											{allowDownload && (
												<Tooltip content="Download" side="top">
													<Button size="icon" variant="ghost" onClick={() => handleDownload(payment)}>
														<IconDownload size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
											{allowPreview && (
												<Tooltip content="Preview" side="top">
													<Button size="icon" variant="ghost" onClick={() => handleClick(payment)}>
														<IconEye size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
											{allowPrint && (
												<Tooltip content="Print" side="top">
													<Button size="icon" variant="ghost">
														<IconPrinter size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
											{allowRemove && (
												<Tooltip content="Remove" side="top">
													<Button size="icon" variant="danger" type="button" onClick={() => handleRemove(data.id!, payment.id)}>
														<IconTrash size={16} stroke={2} />
													</Button>
												</Tooltip>
											)}
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			) : (
				<Blanket width="sm">
					<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
					<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
				</Blanket>
			)}
		</div>
	);
}
